import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

/** Sanitize timezone: system_settings may store display format like
 *  "(UTC-08:00) Pacific Time (US & Canada)" but JS Intl needs IANA like "America/Los_Angeles" */
function sanitizeTimezone(settings) {
  if (!settings || !settings.timezone) return settings;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: settings.timezone });
    return settings; // valid IANA timezone
  } catch {
    return { ...settings, timezone: 'America/Los_Angeles' };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [systemSettings, setSystemSettings] = useState({});

  const loadUser = useCallback(async () => {
    // With httpOnly cookies, we don't check localStorage — just call /me
    // The cookie is sent automatically via withCredentials:true
    try {
      const res = await api.get('/api/auth/me');
      setUser(res.data);
      setSystemSettings(sanitizeTimezone(res.data.system_settings || {}));
      const lang = res.data.language;
      if (lang) {
        const i18n = await import('../i18n').then(m => m.default);
        if (i18n.language !== lang) i18n.changeLanguage(lang);
      }
    } catch (e) {
      if (e?.code !== 'ERR_CANCELED') setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password, tenant_id, force = false) => {
    if (!tenant_id) throw new Error('Company code (tenant_id) is required for login.');
    const res = await api.post('/api/auth/login', { email, password, tenant_id, force });
    // If 2FA is required, return the response without setting user
    if (res.data.requires_2fa) {
      return res.data;
    }
    // H3 FIX: Removed localStorage - relying on httpOnly cookies only
    setUser(res.data.user);
    setSystemSettings(sanitizeTimezone(res.data.user?.system_settings || {}));
    window.dispatchEvent(new Event('eyvo-auth-change'));
    return res.data;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch { /* ignore */ }
    // H3 FIX: Removed localStorage clear - httpOnly cookies are cleared server-side
    setUser(null);
    setSystemSettings({});
  };

  const isAdmin = () => user?.is_admin === true;

  return (
    <AuthContext.Provider value={{ user, loading, systemSettings, login, logout, isAdmin, loadUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
